import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/sections/CTASection";
import { insights } from "@/data/content";
import { articleSchema } from "@/data/seo";

const INSIGHTS_CRUMBS = [{ name: "Insights", path: "/insights" }];
const INSIGHTS_SCHEMA = [
  {
    "@type": "ItemList",
    name: "Insights from HashTag Marketing",
    itemListElement: insights.map((p, i) => ({ "@type": "ListItem", position: i + 1, item: articleSchema(p) })),
  },
];

export default function Insights() {
  useSeo({
    title: "Insights on Branding, PR & Experiential Marketing",
    description:
      "Perspectives on integrated branding, brand launches in India, PR for startups and experiential marketing that earns media, from a Gurgaon agency with 15+ years.",
    path: "/insights",
    pageType: "CollectionPage",
    breadcrumbs: INSIGHTS_CRUMBS,
    schema: INSIGHTS_SCHEMA,
  });
  return (
    <>
      <PageHeader
        eyebrow="Insights"
        titleLines={["Thinking", "out loud."]}
        intro="Points of view on branding, launches, PR, experiential marketing and building brands for Indian audiences, from the team at HashTag."
      />
      <section className="mx-auto max-w-[1600px] px-5 py-12 sm:py-14 md:py-16 lg:py-20 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="grid gap-x-8 gap-y-10 sm:gap-y-12 md:grid-cols-2">
          {insights.map((post, i) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.76, 0, 0.24, 1] }}
              className="group border-t border-line pt-8"
              data-testid={`insight-${post.slug}`}
            >
              <Link to={`/insights/${post.slug}`} className="block">
                <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.14em] text-muted">
                  <span className="text-accent">{post.category}</span>
                  <span>{post.read}</span>
                </div>
                {post.image && (
                  <div className="mt-6 aspect-[16/9] overflow-hidden bg-line">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:grayscale-0"
                    />
                  </div>
                )}
                <h2 className="mt-6 font-display text-3xl font-extrabold leading-tight text-ink/70 transition-colors duration-700 group-hover:text-ink md:text-4xl">
                  {post.title}
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-muted">{post.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-sm">
                  <span className="inline-flex items-center gap-2 font-bold uppercase tracking-[0.14em] text-ink">
                    Read <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                  <span className="text-muted">
                    {post.author} · {post.date}
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
